import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ConditionBadge, AssetStatusBadge } from '@/components/shared/StatusBadge';
import PhotoUpload from '@/components/shared/PhotoUpload';
import { Wrench, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { addActivityLogEntry } from '@/data/mockActivityLog';
import type { Asset, AssetCondition, AssetStatus } from '@/types/models';

interface ProcessAssetDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  asset: Asset | null;
  onUpdated: (asset: Asset) => void;
}

const CONDITIONS: { value: AssetCondition; label: string; colorVar: string }[] = [
  { value: 'new', label: 'New', colorVar: '--condition-new' },
  { value: 'like_new', label: 'Like New', colorVar: '--condition-like-new' },
  { value: 'good', label: 'Good', colorVar: '--condition-good' },
  { value: 'fair', label: 'Fair', colorVar: '--condition-fair' },
  { value: 'poor', label: 'Poor', colorVar: '--condition-poor' },
  { value: 'damaged', label: 'Damaged', colorVar: '--condition-damaged' },
  { value: 'parts_only', label: 'Parts Only', colorVar: '--condition-parts' },
  { value: 'scrap', label: 'Scrap', colorVar: '--condition-scrap' },
];

const PROCESSING_STATUSES: { value: AssetStatus; label: string }[] = [
  { value: 'received', label: 'Received' },
  { value: 'processing', label: 'Processing' },
  { value: 'testing', label: 'Testing' },
  { value: 'sanitized', label: 'Sanitized' },
  { value: 'qa_passed', label: 'QA Passed' },
  { value: 'inventory', label: 'Ready for Sale' },
  { value: 'disposition', label: 'Final Disposition' },
  { value: 'completed', label: 'Completed' },
];

export default function ProcessAssetDialog({ open, onOpenChange, asset, onUpdated }: ProcessAssetDialogProps) {
  const [condition, setCondition] = useState<AssetCondition>(asset?.condition ?? 'good');
  const [status, setStatus] = useState<AssetStatus>(asset?.status ?? 'processing');
  const [notes, setNotes] = useState(asset?.notes ?? '');
  const [photos, setPhotos] = useState<string[]>([]);
  const [disposition, setDisposition] = useState<'sold' | 'recycled' | ''>('');

  // Reset state when asset changes
  if (asset && condition !== asset.condition && status !== asset.status) {
    // This is a new asset being opened
  }

  if (!asset) return null;

  function handleSave() {
    if (!asset) return;

    const finalStatus: AssetStatus = disposition === 'sold' || disposition === 'recycled' ? 'completed' : status;

    const updated: Asset = {
      ...asset,
      condition,
      status: finalStatus,
      notes: notes || undefined,
      updated_at: new Date().toISOString(),
    };

    // Log activity
    if (condition !== asset.condition) {
      addActivityLogEntry({
        entity_type: 'asset',
        entity_id: asset.id,
        user_id: 'current_user',
        user_name: 'Current Operator',
        action: 'condition_updated',
        details: `Condition changed from ${asset.condition} to ${condition}`,
      });
    }
    if (status !== asset.status || finalStatus !== asset.status) {
      addActivityLogEntry({
        entity_type: 'asset',
        entity_id: asset.id,
        user_id: 'current_user',
        user_name: 'Current Operator',
        action: 'status_changed',
        details: `Status changed from ${asset.status} to ${finalStatus}${disposition ? ` (${disposition})` : ''}`,
      });
    }
    if (disposition) {
      addActivityLogEntry({
        entity_type: 'asset',
        entity_id: asset.id,
        user_id: 'current_user',
        user_name: 'Current Operator',
        action: disposition === 'sold' ? 'item_sold' : 'item_recycled',
        details: `Item marked as ${disposition}: ${asset.internal_barcode} (${asset.category_name})`,
      });
    }

    onUpdated(updated);
    toast.success(`Updated ${asset.internal_barcode}`);
    onOpenChange(false);
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Wrench className="h-5 w-5 text-primary" />
            Process: {asset.internal_barcode}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Asset Info */}
          <div className="rounded-lg border border-border p-3 bg-muted/30">
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div>
                <span className="text-muted-foreground">Category:</span>{' '}
                <span className="font-medium text-foreground">{asset.category_name}</span>
              </div>
              <div>
                <span className="text-muted-foreground">Manufacturer:</span>{' '}
                <span className="font-medium text-foreground">{asset.manufacturer_name || '—'}</span>
              </div>
              {asset.serial_number && (
                <div className="col-span-2">
                  <span className="text-muted-foreground">Serial:</span>{' '}
                  <span className="font-mono font-medium text-foreground">{asset.serial_number}</span>
                </div>
              )}
            </div>
            <div className="flex gap-2 mt-2">
              <ConditionBadge condition={asset.condition} />
              <AssetStatusBadge status={asset.status} />
            </div>
          </div>

          {/* Update Condition */}
          <div>
            <Label className="text-xs text-muted-foreground">Update Condition</Label>
            <Select value={condition} onValueChange={(v) => setCondition(v as AssetCondition)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {CONDITIONS.map((c) => (
                  <SelectItem key={c.value} value={c.value}>
                    <div className="flex items-center gap-2">
                      <span
                        className="inline-block h-2.5 w-2.5 rounded-full shrink-0"
                        style={{ backgroundColor: `hsl(var(${c.colorVar}))` }}
                      />
                      {c.label}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Update Status */}
          <div>
            <Label className="text-xs text-muted-foreground">Update Status</Label>
            <Select value={status} onValueChange={(v) => setStatus(v as AssetStatus)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {PROCESSING_STATUSES.map((s) => (
                  <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Final Disposition */}
          <div>
            <Label className="text-xs text-muted-foreground">Final Outcome (optional)</Label>
            <Select value={disposition} onValueChange={(v) => setDisposition(v as 'sold' | 'recycled' | '')}>
              <SelectTrigger><SelectValue placeholder="Not yet determined" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Not yet determined</SelectItem>
                <SelectItem value="sold">
                  <div className="flex items-center gap-2">
                    <span className="inline-block h-2.5 w-2.5 rounded-full bg-status-complete shrink-0" />
                    Sold
                  </div>
                </SelectItem>
                <SelectItem value="recycled">
                  <div className="flex items-center gap-2">
                    <span className="inline-block h-2.5 w-2.5 rounded-full bg-status-processing shrink-0" />
                    Recycled
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Notes */}
          <div>
            <Label className="text-xs text-muted-foreground">Processing Notes</Label>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} placeholder="Inspection findings, test results..." />
          </div>

          {/* Photos */}
          <PhotoUpload photos={photos} onPhotosChange={setPhotos} label="Processing Photos" />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSave} className="gap-2">
            <CheckCircle2 className="h-4 w-4" /> Save Changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
